import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Checkbox, Input, Button, Divider, Spin, Empty, Space, Tag } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../../variables/api/axiosInstance';
import PriseEnCharge from '../../../docs/PriseEnCharge';

/**
 * Sélection des DOCUMENTS À RÉCLAMER sur l'attestation de prise en charge (bloc « Vous voudrez
 * bien nous adresser : … ») — analogue au mandat d'investigation, mais liste PLATE.
 * Charge le catalogue paramétrable, pré-coche la sélection déjà enregistrée (ou les documents
 * « par défaut »), permet d'ajouter des lignes libres, enregistre le snapshot des intitulés sur la
 * PEC (réimprimable à l'identique), avec aperçu live du document.
 */
const DocumentsAReclamerModal = ({ open, setOpen, priseEnCharge, sinistre, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [catalogue, setCatalogue] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [lignesLibres, setLignesLibres] = useState([]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        axiosInstance.get('/api/document-a-reclamer')
            .then((res) => {
                const cat = Array.isArray(res.data) ? res.data : [];
                setCatalogue(cat);

                // Pré-remplissage : sélection déjà enregistrée (snapshot d'intitulés) sinon les « par défaut ».
                let savedLignes = null;
                try {
                    const parsed = priseEnCharge?.documentsAReclamerJson
                        ? JSON.parse(priseEnCharge.documentsAReclamerJson) : null;
                    savedLignes = Array.isArray(parsed?.lignes) ? parsed.lignes.map((s) => String(s || '').trim()) : null;
                } catch (_) { savedLignes = null; }

                if (savedLignes && savedLignes.length) {
                    const parIntitule = new Map(cat.map((c) => [String(c.intitule || '').trim(), c]));
                    const sel = new Set();
                    const libres = [];
                    savedLignes.filter(Boolean).forEach((l) => {
                        const c = parIntitule.get(l);
                        if (c) sel.add(c.codeId); else libres.push(l);
                    });
                    setSelected(sel);
                    setLignesLibres(libres);
                } else {
                    setSelected(new Set(cat.filter((c) => c.parDefaut).map((c) => c.codeId)));
                    setLignesLibres([]);
                }
            })
            .catch(() => toast.error('Erreur lors du chargement du catalogue des documents'))
            .finally(() => setLoading(false));
    }, [open, priseEnCharge]);

    const toggle = (codeId, checked) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(codeId); else next.delete(codeId);
            return next;
        });
    };

    // Lignes finales = intitulés cochés (dans l'ordre du catalogue) + lignes libres non vides.
    const lignes = useMemo(() => {
        const desCatalogue = catalogue
            .filter((c) => selected.has(c.codeId))
            .map((c) => String(c.intitule || '').trim())
            .filter(Boolean);
        const libres = lignesLibres.map((l) => String(l || '').trim()).filter(Boolean);
        return [...desCatalogue, ...libres];
    }, [catalogue, selected, lignesLibres]);

    // PEC pour l'aperçu : on injecte la sélection courante (live).
    const pecApercu = useMemo(
        () => ({ ...(priseEnCharge || {}), documentsAReclamerJson: JSON.stringify({ lignes }) }),
        [priseEnCharge, lignes]
    );

    const handleSave = async () => {
        if (!priseEnCharge?.codeId) { toast.error('Prise en charge introuvable'); return; }
        setSaving(true);
        try {
            await axiosInstance.put(`/api/prise-en-charge/${priseEnCharge.codeId}/documents`, {
                documentsAReclamerJson: JSON.stringify({ lignes }),
            });
            toast.success('Documents à réclamer enregistrés');
            if (onSaved) onSaved();
            setOpen(false);
        } catch (_) {
            toast.error("Échec de l'enregistrement de la sélection");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={<span style={{ color: '#242166' }}>Documents à réclamer — attestation de prise en charge</span>}
            open={open}
            onCancel={() => setOpen(false)}
            footer={null}
            width={1100}
            zIndex={2000}
            centered
            styles={{ body: { maxHeight: '78vh', overflowY: 'auto' } }}
        >
            <Spin spinning={loading}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    {/* Colonne sélection */}
                    <div>
                        <p style={{ color: '#64748b', marginBottom: 12 }}>
                            Cochez, selon le centre de soins / prestataire, les documents à réclamer. Seuls les
                            documents cochés apparaîtront sur l'attestation.
                        </p>
                        {catalogue.length === 0 && !loading && (
                            <Empty description="Aucun document paramétré" />
                        )}
                        {catalogue.map((c) => (
                            <div key={c.codeId} style={{ marginBottom: 8 }}>
                                <Checkbox
                                    checked={selected.has(c.codeId)}
                                    onChange={(e) => toggle(c.codeId, e.target.checked)}
                                >
                                    {c.intitule}
                                </Checkbox>
                            </div>
                        ))}

                        <Divider style={{ margin: '12px 0' }} />
                        <div style={{ fontWeight: 600, color: '#242166', marginBottom: 6 }}>Lignes libres</div>
                        {lignesLibres.map((l, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                <Input
                                    value={l}
                                    placeholder="Document supplémentaire à réclamer"
                                    onChange={(e) => setLignesLibres((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))}
                                />
                                <Button icon={<DeleteOutlined />} danger
                                    onClick={() => setLignesLibres((prev) => prev.filter((_, j) => j !== i))} />
                            </div>
                        ))}
                        <Button size="small" icon={<PlusOutlined />}
                            onClick={() => setLignesLibres((prev) => [...prev, ''])}>
                            Ajouter une ligne
                        </Button>

                        <div style={{ marginTop: 16 }}>
                            <Space>
                                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave} style={{ background: '#242166' }}>
                                    Enregistrer
                                </Button>
                                <Tag color="blue">{lignes.length} document(s)</Tag>
                            </Space>
                        </div>
                    </div>

                    {/* Colonne aperçu */}
                    <div>
                        <Divider style={{ margin: '8px 0' }}>Aperçu</Divider>
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, transform: 'scale(0.82)', transformOrigin: 'top left', width: '122%' }}>
                            <PriseEnCharge priseEnCharge={pecApercu} sinistre={sinistre} />
                        </div>
                    </div>
                </div>
            </Spin>
        </Modal>
    );
};

DocumentsAReclamerModal.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    priseEnCharge: PropTypes.object,
    sinistre: PropTypes.object,
    onSaved: PropTypes.func,
};

export default DocumentsAReclamerModal;
